import { itemApi } from "@api/items";
import { loanApi } from "@api/loans";
import { patronApi } from "@api/patrons";
import { userApi } from "@api/users/user";
import { recordToPidType, withCancel } from "@api/utils";
import { SeeAllButton } from "@components/backoffice/buttons/SeeAllButton";
import { InfoMessage } from "@components/backoffice/InfoMessage";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { ResultsTable } from "@components/ResultsTable/ResultsTable";
import { SearchBarILS } from "@components/SearchBar";
import { getDisplayVal, invenioConfig } from "@config";
import { BackOfficeRoutes } from "@routes/urls";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Header,
  Message,
  Segment,
  Modal,
  Icon,
  Label,
} from "semantic-ui-react";
import { checkBorrowingLimit } from "@utils/CirculationUtils";
import { AuditService } from "@api/audit/audit";

// Constants
const ROLES = {
  FACULTY_ONLY: ["faculty", "admin"],
  ALL_USER_TYPES: ["faculty", "admin", "researcher", "visitor", "student"],
  ADMIN: ["admin", "administrator", "super_admin"],
  STAFF: ["librarian", "admin", "staff", "administrator", "super_admin"],
};

const MESSAGES = {
  RESTRICTIONS: {
    OVERDUE: "Patron has overdue items. ",
    BORROWING_LIMIT: (current, limit) =>
      `Patron has reached borrowing limit (${current}/${limit}). `,
    CHECKOUT_BLOCKED: "Checkout blocked. ",
    OVERRIDE_SUFFIX:
      "The borrower is a faculty member, use the override option.",
    AUTO_CHECKOUT_BLOCKED: "Automatic checkout is blocked. ",
    AUTO_OVERRIDE_SUFFIX: "Contact a librarian to override restrictions.",
    FACULTY_AUTO_OVERRIDE_SUFFIX: "Use the override button to allow checkout.",
  },
  OVERRIDES: {
    ADMIN_ENABLED: "Admin override is enabled",
    FACULTY_ONLY: "Only admins can override restrictions.",
    NO_PERMISSION: "You do not have permission to override restrictions",
    LIBRARIAN_FACULTY:
      "Librarian can override restrictions for faculty members only",
    STUDENT: "Override is not available for students.",
    RESEARCHER: "Override is not available for researchers.",
    VISITOR: "Override is not available for visitors.",
  },
  LOADING: {
    PATRON_STATUS: "Checking patron status...",
    PATRON_ROLE: "Checking patron role...",
  },
  EMPTY_RESULTS: {
    NO_QUERY: {
      header: "No physical copies available.",
      content:
        "Currently document has no eligible physical copies to fulfil this loan.",
    },
    WITH_QUERY: {
      header: "No physical copies found.",
      content:
        "Currently document has no eligible physical copies matching the barcode.",
    },
  },
  CHECKOUT: {
    header: "Copy checkout",
    content: "Scan or paste a barcode to automatically checkout that copy",
  },
  COMPLETED_LOAN:
    "You can't change the physical copy once the loan is completed",
  SEARCH_ERROR:
    "Something went wrong. Please try the search again or contact our support if the problem persists.",
};

const BUTTON_DEFAULTS = {
  size: "mini",
  color: "green",
};

export default class AvailableItems extends Component {
  constructor(props) {
    super(props);
    this.seeAllUrl = BackOfficeRoutes.itemsListWithQuery;
    this.state = {
      query: "",
      filteredData: null,
      modalOpen: false,
      checkoutItem: null,
      patronHasOverdueItems: false,
      isCheckingPatronStatus: false,
      overrideRestrictions: false,
      borrowingLimitExceeded: false,
      borrowingStats: null,
      isCheckingBorrowingLimits: false,
      patronIsFaculty: false,
      isCheckingPatronRole: false,
      patronUserType: null,
      activeLoans: {},
    };

    this._isMounted = false;
    this.cancellableOperations = [];
  }

  componentDidMount() {
    this._isMounted = true;
    const { fetchAvailableItems, loan } = this.props;
    const { patron_pid, document_pid } = loan.metadata;
    fetchAvailableItems(document_pid);
    this.initializePatronChecks(patron_pid);
    this.fetchActiveLoans(document_pid);
  }

  componentWillUnmount() {
    this._isMounted = false;

    this.cancellableOperations.forEach((operation) => {
      if (operation && operation.cancel) {
        operation.cancel();
      }
    });

    this.cancellableOperations = [];

    if (this.cancellableFetch) {
      this.cancellableFetch.cancel();
      this.cancellableFetch = null;
    }
  }

  fetchActiveLoans = async (documentPid) => {
    try {
      const activeStates = invenioConfig.CIRCULATION.loanActiveStates || [
        "ITEM_ON_LOAN",
      ];
      const query = loanApi
        .query()
        .withDocPid(documentPid)
        .withState(activeStates)
        .qs();

      const response = await loanApi.list(query);
      const loansByItemPid = {};

      (response.data.hits || []).forEach((hit) => {
        const loan = hit._source || hit;
        const itemPid =
          loan.metadata?.item_pid?.value || loan.metadata?.item_pid;
        if (itemPid) {
          loansByItemPid[itemPid] = loan;
        }
      });

      this.safeSetState({ activeLoans: loansByItemPid });
    } catch (error) {
      this.safeSetState({ activeLoans: {} });
    }
  };

  safeSetState = (newState, callback) => {
    if (this._isMounted) {
      this.setState(newState, callback);
    }
  };

  initializePatronChecks = (patronPid) => {
    this.checkPatronOverdueStatus(patronPid);
    this.checkPatronBorrowingLimits(patronPid);
    this.checkPatronRole(patronPid);
  };

  extractRoles = (entity) => {
    if (!entity?.roles || !Array.isArray(entity.roles)) {
      return [];
    }
    const extractedRoles = entity.roles
      .map((role) => {
        if (typeof role === "object" && role.name) {
          return role.name.toLowerCase();
        }
        if (typeof role === "string") {
          return role.toLowerCase();
        }
        return "";
      })
      .filter(Boolean);
    return extractedRoles;
  };

  hasAnyRole = (userRoles, targetRoles) => {
    const hasRole = userRoles.some((role) => targetRoles.includes(role));
    return hasRole;
  };

  async determinePatronUserType(patronPid) {
    try {
      const patronResponse = await patronApi.get(patronPid);
      const patronData = patronResponse.data?.metadata;
      const profileId = patronData?.profile_id;
      let userType = null;

      if (profileId && profileId.startsWith("FAC")) {
        userType = "faculty";
      } else if (
        Array.isArray(patronData?.user_types) &&
        patronData.user_types.length > 0
      ) {
        const extractedType = patronData.user_types[0];
        const normalizedType = (
          extractedType.user_type ||
          extractedType.type ||
          extractedType.name ||
          extractedType
        ).toLowerCase();
        if (ROLES.ALL_USER_TYPES.includes(normalizedType)) {
          userType = normalizedType;
        }
      }

      this.safeSetState({ patronUserType: userType });
      return ROLES.FACULTY_ONLY.includes(userType);
    } catch (error) {
      this.safeSetState({ patronUserType: null });
      return false;
    }
  }

  isCurrentUserAdmin = () => {
    const { user } = this.props;
    const userRoles = this.extractRoles(user);
    const isAdmin = this.hasAnyRole(userRoles, ROLES.ADMIN);
    return isAdmin;
  };

  isCurrentUserLibrarian = () => {
    const { user } = this.props;
    const userRoles = this.extractRoles(user);
    const isLibrarian = userRoles.includes("librarian");
    return isLibrarian;
  };

  canPerformOverride = () => {
    const { user } = this.props;
    const userRoles = this.extractRoles(user);
    const canOverride = this.hasAnyRole(userRoles, ROLES.STAFF);
    return canOverride;
  };

  canOverrideRestrictions = () => {
    const { patronIsFaculty } = this.state;
    const isAdmin = this.isCurrentUserAdmin();
    const isLibrarian = this.isCurrentUserLibrarian();
    const canPerformOverride = this.canPerformOverride();
    if (isAdmin) {
      return true;
    }
    if ((isLibrarian || canPerformOverride) && patronIsFaculty) {
      return true;
    }
    return false;
  };

  isPatronRestricted = () => {
    const {
      patronHasOverdueItems,
      borrowingLimitExceeded,
      overrideRestrictions,
    } = this.state;
    return (
      (patronHasOverdueItems || borrowingLimitExceeded) && !overrideRestrictions
    );
  };

  canPatronCheckout = () => {
    const {
      patronHasOverdueItems,
      borrowingLimitExceeded,
      overrideRestrictions,
    } = this.state;
    const hasRestrictions = patronHasOverdueItems || borrowingLimitExceeded;
    if (!hasRestrictions) {
      return true;
    }
    if (hasRestrictions && overrideRestrictions) {
      return true;
    }
    return false;
  };

  isItemAvailable = (item) => {
    if (!item?.metadata) return false;

    const { activeLoans } = this.state;
    const itemPid = item.metadata.pid;

    if (activeLoans && activeLoans[itemPid]) {
      return false;
    }

    if (item.metadata.circulation?.state === "ITEM_ON_LOAN") {
      return false;
    }

    const status = item.metadata.status;
    const onLoanStatuses = ["ITEM_ON_LOAN"];
    if (onLoanStatuses.includes(status)) {
      return false;
    }

    return true;
  };

  async checkPatronRole(patronPid) {
    this.safeSetState({ isCheckingPatronRole: true });
    try {
      const patronIsFaculty = await this.determinePatronUserType(patronPid);
      this.safeSetState({ patronIsFaculty, isCheckingPatronRole: false });
    } catch (error) {
      this.safeSetState({
        patronIsFaculty: false,
        isCheckingPatronRole: false,
      });
    }
  }

  async checkPatronBorrowingLimits(patronPid) {
    this.safeSetState({ isCheckingBorrowingLimits: true });
    try {
      const stats = await checkBorrowingLimit(patronPid);
      this.safeSetState({
        borrowingLimitExceeded: !stats.canBorrow,
        borrowingStats: stats,
        isCheckingBorrowingLimits: false,
      });
    } catch (error) {
      this.safeSetState({ isCheckingBorrowingLimits: false });
    }
  }

  async checkPatronOverdueStatus(patronPid) {
    this.safeSetState({ isCheckingPatronStatus: true });
    try {
      const query = loanApi
        .query()
        .overdue()
        .withPatronPid(patronPid)
        .withState(invenioConfig.CIRCULATION.loanActiveStates)
        .qs();

      const cancellableFetch = withCancel(loanApi.list(query));
      this.cancellableOperations.push(cancellableFetch);
      this.cancellableFetch = cancellableFetch;

      const response = await cancellableFetch.promise;
      this.safeSetState({
        patronHasOverdueItems: response.data.total > 0,
        isCheckingPatronStatus: false,
      });
    } catch (error) {
      if (
        error.name !== "AbortError" &&
        error.message !== "Cancelled" &&
        error !== "UNMOUNTED"
      ) {
        console.error("Error checking patron overdue status:", error);
      }
      this.safeSetState({ isCheckingPatronStatus: false });
    }
  }

  seeAllButton = () => {
    const {
      loan: {
        metadata: { document_pid },
      },
    } = this.props;
    const path = this.seeAllUrl(itemApi.query().withDocPid(document_pid).qs());
    return <SeeAllButton to={path} />;
  };

  isCheckingPatronStatus = () => {
    const { isCheckingPatronStatus, isCheckingBorrowingLimits } = this.state;
    return isCheckingPatronStatus || isCheckingBorrowingLimits;
  };

  getRestrictionMessage = () => {
    const { patronHasOverdueItems, borrowingLimitExceeded, borrowingStats } =
      this.state;
    let message = "";
    if (patronHasOverdueItems) {
      message += MESSAGES.RESTRICTIONS.OVERDUE;
    }
    if (borrowingLimitExceeded && borrowingStats) {
      message += MESSAGES.RESTRICTIONS.BORROWING_LIMIT(
        borrowingStats.current,
        borrowingStats.limit
      );
    }
    return message;
  };

  createActionButton = ({
    text,
    color = BUTTON_DEFAULTS.color,
    size = BUTTON_DEFAULTS.size,
    onClick,
    disabled = false,
    loading = false,
    icon = null,
  }) => (
    <Button
      size={size}
      color={color}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      {icon && <Icon name={icon} />}
      {text}
    </Button>
  );

  assignItemButton = (item) => {
    const {
      loan: { pid },
      assignItemToLoan,
    } = this.props;
    const { activeLoans } = this.state;

    const itemPid = item.metadata.pid;
    const hasActiveLoan = activeLoans && activeLoans[itemPid];
    const itemAvailable = this.isItemAvailable(item);

    const disableReplace =
      !itemAvailable ||
      !this.canPatronCheckout() ||
      this.isCheckingPatronStatus();

    const buttonText = hasActiveLoan || !itemAvailable ? "On Loan" : "Replace";

    return (
      <div>
        {this.createActionButton({
          text: buttonText,
          color: hasActiveLoan ? "red" : BUTTON_DEFAULTS.color,
          loading: this.isCheckingPatronStatus(),
          disabled: disableReplace,
          onClick: () => {
            const itemPid = {
              type: recordToPidType(item),
              value: item.metadata.pid,
            };
            assignItemToLoan(itemPid, pid);
          },
        })}
      </div>
    );
  };

  async performCheckout(loan, item = null) {
    const { performCheckoutAction, sendErrorNotification } = this.props;
    const { checkoutItem, modalOpen, patronIsFaculty } = this.state;
    item = item || checkoutItem;
    if (modalOpen) {
      this.safeSetState({ modalOpen: false });
    }
    if (!this.canPatronCheckout()) {
      const restrictionMessage = this.getRestrictionMessage();
      let errorMessage =
        MESSAGES.RESTRICTIONS.CHECKOUT_BLOCKED + restrictionMessage;
      if (patronIsFaculty) {
        errorMessage += MESSAGES.RESTRICTIONS.OVERRIDE_SUFFIX;
      } else {
        errorMessage += MESSAGES.RESTRICTIONS.AUTO_OVERRIDE_SUFFIX;
      }
      sendErrorNotification("Checkout Blocked", errorMessage);
      return;
    }
    const checkoutUrl = loan.availableActions.checkout;
    const patronPid = loan.metadata.patron_pid;
    const documentPid = item.metadata.document.pid;
    const itemPid = {
      type: recordToPidType(item),
      value: item.metadata.pid,
    };
    try {
      const response = await userApi.get();
      const user = response?.data || response;
      const userName = user.full_name || "Unknown User";
      const userRole = user.roles?.[0] || "patron";
      const patronName = loan.metadata.patron.name || "Unknown Patron";
      const itemTitle = item.metadata.document.title || "Untitled Document";
      const barcode = item.metadata.barcode || "Unknown";
      const details = `The user ${userName} checked out the item "${itemTitle}" with barcode ${barcode} for ${patronName} at ${new Date().toLocaleString()}.`;
      await AuditService.api.createLog({
        action: "Checkout Item",
        details,
        created_by: `${userName} Role : (${userRole})`,
      });
    } catch (error) {
      console.error("Audit log for checkout failed:", error);
    }
    performCheckoutAction(checkoutUrl, documentPid, patronPid, itemPid);
  }

  handleCloseModal = () => {
    this.safeSetState({ modalOpen: false });
  };

  checkoutItemButton = (item, loan) => {
    const { isActionLoading } = this.props;
    const { activeLoans } = this.state;

    const itemPid = item.metadata.pid;
    const hasActiveLoan = activeLoans && activeLoans[itemPid];
    const itemAvailable = this.isItemAvailable(item);

    const disableCheckout =
      !itemAvailable ||
      !this.canPatronCheckout() ||
      isActionLoading ||
      this.isCheckingPatronStatus();

    const buttonText = hasActiveLoan || !itemAvailable ? "On Loan" : "Checkout";

    return (
      <div>
        {this.createActionButton({
          text: buttonText,
          color: hasActiveLoan ? "red" : BUTTON_DEFAULTS.color,
          loading: isActionLoading || this.isCheckingPatronStatus(),
          disabled: disableCheckout,
          onClick: () => {
            if (!loan["is_first_requested"]) {
              this.safeSetState({ modalOpen: true, checkoutItem: item });
            } else {
              this.performCheckout(loan, item);
            }
          },
        })}
      </div>
    );
  };

  renderLoadingMessage = (text) => (
    <Message compact info size="tiny">
      <Icon name="spinner" loading />
      {text}
    </Message>
  );

  renderInfoMessage = (text, iconName = "lock") => (
    <Message compact info size="tiny" style={{ marginTop: "10px" }}>
      <Icon name={iconName} />
      {text}
    </Message>
  );

  getOverrideMessage = () => {
    const { patronIsFaculty, patronUserType } = this.state;
    const isAdmin = this.isCurrentUserAdmin();
    const isLibrarian = this.isCurrentUserLibrarian();
    const canPerformOverride = this.canPerformOverride();

    if (isAdmin) return MESSAGES.OVERRIDES.ADMIN_ENABLED;
    if ((isLibrarian || canPerformOverride) && patronIsFaculty)
      return MESSAGES.OVERRIDES.LIBRARIAN_FACULTY;
    if (!canPerformOverride) return MESSAGES.OVERRIDES.NO_PERMISSION;

    switch (patronUserType) {
      case "faculty":
        return MESSAGES.OVERRIDES.LIBRARIAN_FACULTY;
      case "student":
        return MESSAGES.OVERRIDES.STUDENT;
      case "researcher":
        return MESSAGES.OVERRIDES.RESEARCHER;
      case "visitor":
        return MESSAGES.OVERRIDES.VISITOR;
      default:
        return MESSAGES.OVERRIDES.NO_PERMISSION;
    }
  };

  renderRestrictionMessage = () => {
    const { data } = this.props;
    const {
      patronHasOverdueItems,
      borrowingLimitExceeded,
      overrideRestrictions,
      isCheckingPatronRole,
    } = this.state;
    const hasRestrictions = patronHasOverdueItems || borrowingLimitExceeded;
    const hasItems = data.hits && data.hits.length > 0;
    const canOverride = this.canOverrideRestrictions();

    if (!hasItems) return null;
    if (hasRestrictions && !overrideRestrictions) {
      return (
        <Message warning>
          <Message.Header>Checkout Restricted</Message.Header>
          <p>{this.getRestrictionMessage()}</p>
          {isCheckingPatronRole
            ? this.renderLoadingMessage(MESSAGES.LOADING.PATRON_ROLE)
            : canOverride
            ? this.createActionButton({
                text: "Override Restrictions",
                color: "orange",
                size: "small",
                icon: "unlock",
                onClick: () =>
                  this.safeSetState({ overrideRestrictions: true }),
              })
            : this.renderInfoMessage(this.getOverrideMessage())}
        </Message>
      );
    }
    if (hasRestrictions && overrideRestrictions) {
      return (
        <Message positive>
          <Message.Header>Restrictions Overridden</Message.Header>
          <p>{this.getRestrictionMessage()}</p>
          {canOverride &&
            this.createActionButton({
              text: "Restore Restrictions",
              icon: "lock",
              size: "small",
              onClick: () => this.safeSetState({ overrideRestrictions: false }),
            })}
        </Message>
      );
    }
    return null;
  };

  getTableColumns = () => {
    return [
      { title: "PID", field: "metadata.pid" },
      {
        title: "Barcode",
        field: "metadata.barcode",
        formatter: ({ row }) => (
          <Link to={BackOfficeRoutes.itemDetailsFor(row.metadata.pid)}>
            {row.metadata.barcode}
          </Link>
        ),
      },
      {
        title: "Status",
        field: "metadata.status",
        formatter: ({ row }) =>
          getDisplayVal("ITEMS.statuses", row.metadata.status),
      },
      { title: "Medium", field: "metadata.medium" },
      { title: "Location", field: "metadata.internal_location.name" },
      { title: "Shelf", field: "metadata.shelf" },
      {
        title: "Actions",
        field: "",
        formatter: this.rowActionButton,
      },
    ];
  };

  renderTable() {
    const { data } = this.props;
    const { filteredData } = this.state;
    return (
      <>
        <ResultsTable
          data={filteredData || data.hits}
          columns={this.getTableColumns()}
          totalHitsCount={data.total}
          name="available physical copies"
          seeAllComponent={this.seeAllButton()}
          renderEmptyResultsElement={this.renderEmptyResults}
        />
        {this.renderRestrictionMessage()}
      </>
    );
  }

  renderEmptyResults = () => {
    const { query } = this.state;
    const messageConfig = _isEmpty(query)
      ? MESSAGES.EMPTY_RESULTS.NO_QUERY
      : MESSAGES.EMPTY_RESULTS.WITH_QUERY;
    return (
      <div className="pt-default pb-default">
        <InfoMessage
          header={messageConfig.header}
          content={messageConfig.content}
        />
      </div>
    );
  };

  getRowActionByLoanState = (loan, row) => {
    const { state } = loan.metadata;
    const isRequested =
      invenioConfig.CIRCULATION.loanRequestStates.includes(state);
    const isCompleted =
      invenioConfig.CIRCULATION.loanCompletedStates.includes(state);
    if (isRequested) return this.checkoutItemButton(row, loan);
    if (isCompleted)
      return this.renderInfoMessage(MESSAGES.COMPLETED_LOAN, "info");
    return this.assignItemButton(row);
  };

  rowActionButton = ({ row }) => {
    const { loan } = this.props;
    if (this.isCheckingPatronStatus()) {
      return this.renderLoadingMessage(MESSAGES.LOADING.PATRON_STATUS);
    }
    return this.getRowActionByLoanState(loan, row);
  };

  onChangeHandler = (query) => {
    const { data } = this.props;
    if (query) {
      const barcode = query.trim();
      const filteredData = data.hits.filter((hit) =>
        hit.metadata.barcode.startsWith(barcode)
      );
      this.safeSetState({ query, filteredData });
    } else {
      this.safeSetState({ query: "", filteredData: null });
    }
  };

  searchByBarcode = async (barcode, documentPid) => {
    const { sendErrorNotification } = this.props;
    try {
      const response = await itemApi.list(
        itemApi
          .query()
          .withDocPid(documentPid)
          .withStatus(invenioConfig.ITEMS.canCirculateStatuses)
          .availableForCheckout()
          .withBarcode(barcode)
          .qs()
      );
      return response.data;
    } catch (error) {
      sendErrorNotification("Search for barcode", MESSAGES.SEARCH_ERROR);
    }
  };

  onSearchExecute = async (query) => {
    const { loan, sendErrorNotification } = this.props;
    const { patronIsFaculty } = this.state;
    if (!query) return;
    const barcode = query.trim();
    const results = await this.searchByBarcode(
      barcode,
      loan.metadata.document_pid
    );
    const isRequested = invenioConfig.CIRCULATION.loanRequestStates.includes(
      loan.metadata.state
    );
    if (results && results.total === 1 && isRequested) {
      const item = results.hits[0];
      if (this.canPatronCheckout()) {
        this.performCheckout(loan, item);
      } else {
        const restrictionMessage = this.getRestrictionMessage();
        let errorMessage =
          MESSAGES.RESTRICTIONS.AUTO_CHECKOUT_BLOCKED + restrictionMessage;
        if (patronIsFaculty) {
          errorMessage += MESSAGES.RESTRICTIONS.FACULTY_AUTO_OVERRIDE_SUFFIX;
        } else {
          errorMessage += MESSAGES.RESTRICTIONS.AUTO_OVERRIDE_SUFFIX;
        }
        sendErrorNotification("Checkout blocked", errorMessage);
      }
    }
  };

  renderCheckoutMessage = () => (
    <InfoMessage
      header={MESSAGES.CHECKOUT.header}
      content={MESSAGES.CHECKOUT.content}
    />
  );

  renderSearchBar = () => {
    const disableSearch = this.isCheckingPatronStatus();
    return (
      <SearchBarILS
        onChangeHandler={this.onChangeHandler}
        onSearchHandler={this.onSearchExecute}
        placeholder="Insert barcode..."
        disabled={disableSearch}
      />
    );
  };

  renderPatronWarningLabel = () => {
    const {
      patronHasOverdueItems,
      borrowingLimitExceeded,
      overrideRestrictions,
    } = this.state;
    if (!patronHasOverdueItems && !borrowingLimitExceeded) return null;
    const warnings = [];
    if (patronHasOverdueItems) warnings.push("Overdue items");
    if (borrowingLimitExceeded) warnings.push("Borrowing limit");
    return (
      <Label color="orange" size="tiny" style={{ marginLeft: "1rem" }}>
        <Icon name="warning" />
        {warnings.join(" · ")}
        {overrideRestrictions && " (Overridden)"}
      </Label>
    );
  };

  getRenderingConditions = (loanState) => {
    const statesThatRenderSearchBar = [
      ...invenioConfig.CIRCULATION.loanActiveStates,
      ...invenioConfig.CIRCULATION.loanRequestStates,
    ];
    return {
      shouldRenderSearchBar: statesThatRenderSearchBar.includes(loanState),
      shouldRenderCheckoutMessage:
        invenioConfig.CIRCULATION.loanRequestStates.includes(loanState),
    };
  };

  render() {
    const { isLoading, error, loan, data, firstLoanRequested } = this.props;
    const { modalOpen } = this.state;
    const { shouldRenderSearchBar, shouldRenderCheckoutMessage } =
      this.getRenderingConditions(loan.metadata.state);
    const showSearchBar = shouldRenderSearchBar && !_isEmpty(data.hits);
    return (
      <>
        <Header as="h3" attached="top">
          Available physical copies
          {showSearchBar && this.renderPatronWarningLabel()}
        </Header>
        {showSearchBar && (
          <Segment attached>
            {shouldRenderCheckoutMessage && this.renderCheckoutMessage()}
            {this.renderSearchBar()}
          </Segment>
        )}
        <Segment
          attached
          className="bo-metadata-segment no-padding"
          id="available-items"
        >
          <Loader isLoading={isLoading}>
            <Error error={error}>{this.renderTable()}</Error>
            {firstLoanRequested && (
              <Modal open={modalOpen}>
                <Modal.Header>Preceding loan request found!</Modal.Header>
                <Modal.Content>
                  There is a preceding{" "}
                  <Link
                    target="_blank"
                    to={BackOfficeRoutes.loanDetailsFor(firstLoanRequested.pid)}
                  >
                    pending loan request{" "}
                  </Link>
                  for this literature. Are you sure that you want to checkout
                  the copy for this request?
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={this.handleCloseModal}>Cancel</Button>
                  <Button
                    primary
                    icon="checkmark"
                    labelPosition="left"
                    content="Checkout"
                    onClick={() => this.performCheckout(loan)}
                    disabled={!this.canPatronCheckout()}
                  />
                </Modal.Actions>
              </Modal>
            )}
          </Loader>
        </Segment>
      </>
    );
  }
}

AvailableItems.propTypes = {
  assignItemToLoan: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  loan: PropTypes.object.isRequired,
  firstLoanRequested: PropTypes.object,
  fetchAvailableItems: PropTypes.func.isRequired,
  performCheckoutAction: PropTypes.func.isRequired,
  sendErrorNotification: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  isActionLoading: PropTypes.bool,
  error: PropTypes.object,
};

AvailableItems.defaultProps = {
  isLoading: false,
  isActionLoading: false,
  error: {},
  firstLoanRequested: null,
};
